import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Paper, Grid, Stack, TextField, FormControlLabel, Checkbox, Button, Typography } from '@mui/material';
import { ArrowForwardIos, RestartAlt, Work } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';


const initialValues = {
  role_name: '',
  role_description: '',
  status: true,
};

function EmployeeRoleForm({ initialData, onSuccess }) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  const isEditMode = !!(initialData && initialData.role_id);

  const RequiredLabel = ({ label }) => (
    <span>
      {label}
      <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
    </span>
  );
  

  useEffect(() => {
    if (isEditMode) {
      setValues({
        role_name: initialData.role_name || '',
        role_description: initialData.role_description || '',
        status: initialData.status === 1,
      });
    } else {
      setValues(initialValues);
    }
  }, [initialData, isEditMode]);

  const validate = () => {
    const temp = {};
    temp.role_name = values.role_name ? '' : 'Role name is required.';
    // Active field is required (must be explicitly true/false, not null/undefined)
    temp.status =
      values.status === true || values.status === false || values.status === 1 || values.status === 0
        ? ''
        : 'Active is required.';
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const resetForm = () => {
    if (isEditMode && initialData) {
      setValues({
        role_name: initialData.role_name || '',
        role_description: initialData.role_description || '',
        status: initialData.status === 1,
      });
    } else {
      setValues(initialValues);
    }
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      role_name: values.role_name,
      role_description: values.role_description,
      status: Number(values.status),
    };

    if (isEditMode && initialData.role_id) {
      payload.role_id = initialData.role_id;
    }

    const url = isEditMode
      ? `${process.env.REACT_APP_API_BASE_URL}/company-master/edit-employee-roles`
      : `${process.env.REACT_APP_API_BASE_URL}/company-master/add-employee-roles`;

    try {
      const response = await axios.post(url, payload);
      if (response.status === 200) {
        setNotify({
          isOpen: true,
          message: response.data.message || (isEditMode ? 'Role updated successfully' : 'Role added successfully'),
          type: 'success',
        });
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.message || 'Failed to save role',
          type: 'error',
        });
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '-15px', padding: '30px', fontWeight: '500px' }}>
      <Paper elevation={3} sx={{ width: '600px', marginTop: '-10px' }}>
        <Grid className="form-title" container spacing={2}>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center', margin: 2 }}>
              {isEditMode ? 'Edit Employee Role' : 'Add Employee Role'}
            </Typography>
          </Grid>
          <Grid item>
            <Work sx={{ width: 40, height: 40, marginLeft: '-20px' }} />
          </Grid>
        </Grid>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: '30px' }}>
            <TextField
              variant="outlined"
              name="role_name"
              label={<RequiredLabel label="Role Name" />}
              size="small"
              value={values.role_name}
              onChange={handleInputChange}
              {...(errors.role_name && { error: true, helperText: errors.role_name })}
            />

            <TextField
              variant="outlined"
              name="role_description"
              label="Role Description"
              size="small"
              multiline
              minRows={2}
              value={values.role_description}
              onChange={handleInputChange}
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="status"
                  color="primary"
                  checked={Boolean(values.status)}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      status: e.target.checked,
                    }))
                  }
                />
              }
              label={
                <span>
                  Active<span style={{ color: 'red', marginLeft: 3 }}>*</span>
                </span>
              }
            />

            <Grid container justifyContent="space-between">
              <Grid item>
                <Button
                  className="form-btn"
                  variant="contained"
                  color="error"
                  onClick={resetForm}
                  endIcon={<RestartAlt />}
                >
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button
                  className="form-btn"
                  variant="contained"
                  type="submit"
                  endIcon={<ArrowForwardIos sx={{ width: 18, height: 18 }} />}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default EmployeeRoleForm;

